/**
 * SpokeHub — renders a central AI circle hub with SVG spoke lines connecting
 * to each item in the left and right columns. Uses ResizeObserver to measure
 * real DOM positions so lines are always accurate regardless of content height.
 *
 * Usage:
 *   <SpokeHub
 *     left={[<Card />, <Card />]}   // items connected to left of hub
 *     right={[<Card />, <Card />]}  // items connected to right of hub
 *   />
 *
 * On desktop (≥640px): horizontal layout — left items | spokes | hub | spokes | right items
 * On mobile (<640px):  vertical layout — left items stacked, hub, right items stacked,
 *                       with vertical spoke lines
 */

import { useRef, useEffect, useState, useCallback, type ReactNode } from 'react'

const aiIcon = '/assets/ai-icon.svg'

// ─── Types ───────────────────────────────────────────────────────────────────

interface SpokeHubProps {
  left: ReactNode[]
  right: ReactNode[]
}

interface Rect {
  top: number
  bottom: number
  left: number
  right: number
  width: number
  height: number
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function relativeRect(el: HTMLElement, container: HTMLElement): Rect {
  const er = el.getBoundingClientRect()
  const cr = container.getBoundingClientRect()
  return {
    top: er.top - cr.top,
    bottom: er.bottom - cr.top,
    left: er.left - cr.left,
    right: er.right - cr.left,
    width: er.width,
    height: er.height,
  }
}

// Arrow marker id (unique per mount to avoid SVG marker conflicts)
let _markerSeq = 0

// ─── Component ───────────────────────────────────────────────────────────────

export function SpokeHub({ left, right }: SpokeHubProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const hubRef = useRef<HTMLDivElement>(null)
  const leftRefs = useRef<(HTMLDivElement | null)[]>([])
  const rightRefs = useRef<(HTMLDivElement | null)[]>([])
  const markerId = useRef(`spoke-arrow-${++_markerSeq}`)

  const [lines, setLines] = useState<
    { x1: number; y1: number; x2: number; y2: number; fromHub: boolean }[]
  >([])
  const [svgSize, setSvgSize] = useState({ w: 0, h: 0 })

  const recompute = useCallback(() => {
    const container = containerRef.current
    const hub = hubRef.current
    if (!container || !hub) return

    const cw = container.offsetWidth
    const ch = container.offsetHeight
    setSvgSize({ w: cw, h: ch })

    const hubRect = relativeRect(hub, container)
    const hubCx = hubRect.left + hubRect.width / 2
    const hubCy = hubRect.top + hubRect.height / 2

    const newLines: typeof lines = []

    // Left items → hub center
    leftRefs.current.forEach((el) => {
      if (!el) return
      const r = relativeRect(el, container)
      const itemCx = r.left + r.width / 2
      const itemCy = r.top + r.height / 2
      newLines.push({ x1: itemCx, y1: itemCy, x2: hubCx, y2: hubCy, fromHub: false })
    })

    // Hub center → right items
    rightRefs.current.forEach((el) => {
      if (!el) return
      const r = relativeRect(el, container)
      const itemCx = r.left + r.width / 2
      const itemCy = r.top + r.height / 2
      newLines.push({ x1: hubCx, y1: hubCy, x2: itemCx, y2: itemCy, fromHub: true })
    })

    setLines(newLines)
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    recompute()

    const ro = new ResizeObserver(recompute)
    ro.observe(container)

    // Also recompute on fonts/images loaded
    window.addEventListener('resize', recompute)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', recompute)
    }
  }, [recompute, left.length, right.length])

  // Ensure refs arrays are right length
  leftRefs.current = leftRefs.current.slice(0, left.length)
  rightRefs.current = rightRefs.current.slice(0, right.length)

  return (
    <div ref={containerRef} className="relative w-full">
      {/* SVG overlay — drawn behind everything */}
      {svgSize.w > 0 && (
        <svg
          className="absolute inset-0 pointer-events-none overflow-visible"
          width={svgSize.w}
          height={svgSize.h}
          style={{ zIndex: 0 }}
        >
          <defs>
            {/* Arrow pointing toward destination */}
            <marker
              id={markerId.current}
              markerWidth="8"
              markerHeight="8"
              refX="6"
              refY="3"
              orient="auto"
            >
              <path d="M0,0 L0,6 L8,3 z" fill="#359d9e" opacity="0.5" />
            </marker>
          </defs>

          {lines.map((l, i) => {
            // Shorten line so it doesn't overlap the hub circle or item card edges
            // We shorten by 28px from each end (hub radius ~28px, item edge padding)
            const dx = l.x2 - l.x1
            const dy = l.y2 - l.y1
            const len = Math.sqrt(dx * dx + dy * dy) || 1
            const shrink = 30
            const x1 = l.x1 + (dx / len) * shrink
            const y1 = l.y1 + (dy / len) * shrink
            const x2 = l.x2 - (dx / len) * shrink
            const y2 = l.y2 - (dy / len) * shrink

            return (
              <line
                key={i}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#359d9e"
                strokeWidth="1.5"
                strokeOpacity="0.45"
                strokeDasharray="4 3"
                markerEnd={`url(#${markerId.current})`}
              />
            )
          })}
        </svg>
      )}

      {/* Layout: desktop horizontal, mobile vertical */}
      <div className="relative flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:justify-center sm:gap-6" style={{ zIndex: 1 }}>

        {/* Left items */}
        <div className="flex flex-col gap-3 flex-shrink-0">
          {left.map((child, i) => (
            <div
              key={i}
              ref={(el) => { leftRefs.current[i] = el }}
            >
              {child}
            </div>
          ))}
        </div>

        {/* Hub — the Pelajari AI circle */}
        <div
          ref={hubRef}
          className="flex-shrink-0 relative z-10 w-16 h-16 rounded-full bg-gradient-to-b from-[#359d9e] to-[#4dcccd] flex items-center justify-center shadow-[0_4px_20px_rgba(53,157,158,0.35)]"
        >
          <img src={aiIcon} alt="Pelajari AI" className="w-8 h-8 object-contain" />
          {/* Subtle pulse ring */}
          <span className="absolute inset-0 rounded-full border-2 border-[#359d9e] opacity-20 animate-ping" />
        </div>

        {/* Right items */}
        <div className="flex flex-col gap-3 flex-shrink-0">
          {right.map((child, i) => (
            <div
              key={i}
              ref={(el) => { rightRefs.current[i] = el }}
            >
              {child}
            </div>
          ))}
        </div>

      </div>
    </div>
  )
}
